import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box,
  Chip,
  Stack,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  EditNote as DraftsIcon,
  ArrowBack as BackIcon,
  FolderShared as SharedIcon,
  Assignment as ReportsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  MedicalServices as ProvidersIcon,
  Badge as StaffIcon,
  AdminPanelSettings as AdminRoleIcon,
  PersonAdd as PersonAddIcon,
  SettingsOutlined as ProfileSettingsIcon,
  EventAvailable as QueueIcon,
  EventNote as AppointmentIcon,
  Insights as InsightsIcon,
  TollOutlined as CreditIcon,
  Science as ProtocolIcon,
  AccountTree as SitesIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { NotificationsProvider } from '../contexts/NotificationsContext';
import NotificationBell from './NotificationBell';
import PortalSwitcher from './PortalSwitcher';
import PlatformEntryBanner from './PlatformEntryBanner';
import SiteContext from './SiteContext';

const drawerWidth = 276;
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isPlatformEntry } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);
  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  const handleSettings = () => {
    handleProfileMenuClose();
    navigate('/settings');
  };

  const isAdmin = user?.role === 'clinic_admin';
  const isStaff = user?.role === 'staff';
  const isSuperAdmin = user?.role === 'super_admin';
  const isProvider = user?.role === 'provider';
  // An organisation admin runs several sites. Their session names an organisation
  // rather than a clinic, so the clinic-scoped nav below would have nothing to point
  // at — they get their own set of screens instead.
  const isOrgAdmin = user?.role === 'org_admin';
  // Scoped to a level of the hierarchy, not a clinic. Their whole job — see what
  // is beneath you, split your sessions across it — lives on one screen, so they
  // get one nav entry rather than a menu of clinic screens with nothing to load.
  const isNodeAdmin = ['enterprise_admin', 'region_admin'].includes(user?.role);
  // A clinic admin at the organisation's MAIN site can open further sites under it,
  // so they get the Sites entry alongside their ordinary clinic nav — not the whole
  // org menu, which covers people and credits they have no authority over.
  // A platform admin inside a clinic administers it, its organisation included — they
  // are the account expected to fix a customer's structure, so Sites is theirs whichever
  // site they entered through. For an ordinary clinic admin it stays the main site only.
  const managesSites = (isAdmin && !!user?.org_is_top_site) || !!user?.platform_entry;

  // What this person is, not just what they are acting as. Somebody can be a provider
  // AND the clinic's administrator, and the header showing only the active role made
  // the other half of their access invisible. `mbs` is the token's advisory membership
  // summary — display only; every action still re-checks on the server.
  const ROLE_LABELS = {
    provider: 'Provider',
    clinic_admin: 'Admin',
    staff: 'Staff',
    org_admin: 'Org admin',
    enterprise_admin: 'Enterprise admin',
    region_admin: 'Region admin',
    super_admin: 'Platform admin',
  };
  const roleTags = React.useMemo(() => {
    const active = user?.role;
    const here = String(user?.clinic_id || '');
    const held = new Set(active ? [active] : []);
    (Array.isArray(user?.mbs) ? user.mbs : []).forEach((m) => {
      // Roles at OTHER sites belong to the site picker, not to this badge — the tag
      // says what you are here.
      const scope = String(m?.clinic_id || '');
      if (!m?.role) return;
      if (['org_admin', 'enterprise_admin', 'region_admin'].includes(m.role)
          || !here || scope === here) held.add(m.role);
    });
    return [...held]
      .filter((r) => ROLE_LABELS[r])
      .sort((a, b) => (a === active ? -1 : b === active ? 1 : 0))
      .map((r) => ({ label: ROLE_LABELS[r], active: r === active }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.clinic_id, user?.mbs]);

  // Providers only see "Add Patient" when their clinic has opted in (backend enforces too).
  const [providersCanAddPatients, setProvidersCanAddPatients] = useState(false);
  useEffect(() => {
    if (!isProvider) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/v1/admin/clinic-settings`)
      .then(({ data }) => { if (!cancelled) setProvidersCanAddPatients(!!data?.providers_can_add_patients); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isProvider]);

  // Protocol Development is a per-clinic entitlement (super-admin toggle); only
  // surface the nav item when the clinic is switched on (backend enforces too).
  const [protocolEnabled, setProtocolEnabled] = useState(false);
  useEffect(() => {
    if (!(isProvider || isAdmin)) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/v1/protocols/config`)
      .then(({ data }) => { if (!cancelled) setProtocolEnabled(!!data?.enabled); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isProvider, isAdmin]);

  const menuItems = isSuperAdmin
    ? [{ text: 'Dashboard', icon: <DashboardIcon />, path: '/' }]
    : isNodeAdmin
    ? [
        { text: 'Overview', icon: <DashboardIcon />, path: '/' },
        { text: 'AI Sessions', icon: <CreditIcon />, path: '/node/credits' },
        { text: 'People', icon: <PeopleIcon />, path: '/node/people' },
        { text: 'Patients', icon: <SharedIcon />, path: '/node/patients' },
        { text: 'Administrators', icon: <AdminRoleIcon />, path: '/node/admins' },
      ]
    : isOrgAdmin
    ? [
        { text: 'Overview', icon: <DashboardIcon />, path: '/' },
        { text: 'Sites', icon: <SitesIcon />, path: '/org/sites' },
        { text: 'People', icon: <PeopleIcon />, path: '/org/team' },
        { text: 'Patients', icon: <SharedIcon />, path: '/node/patients' },
        // The level-wide page: it shows regions as well as offices, which this one
        // used to leave out, so an organisation could not fund its regions here.
        { text: 'AI Sessions', icon: <CreditIcon />, path: '/node/credits' },
        { text: 'Administrators', icon: <AdminRoleIcon />, path: '/org/admins' },
      ]
    : isStaff
      ? [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
          { text: 'Patients', icon: <PeopleIcon />, path: '/patients' },
          { text: 'Reports', icon: <ReportsIcon />, path: '/reports' },
          { text: 'Today’s Queue', icon: <QueueIcon />, path: '/queue' },
        ]
      : [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
          { text: 'Patients', icon: <PeopleIcon />, path: '/patients' },
          { text: 'Reports', icon: <ReportsIcon />, path: '/reports' },
          // Unfinished visits whose recording has expired leave the dashboard card but
          // not the product; this is where they are found.
          { text: 'Drafts', icon: <DraftsIcon />, path: '/drafts' },
          ...(protocolEnabled
            ? [{ text: 'Protocols', icon: <ProtocolIcon />, path: '/protocols' }]
            : []),
          { text: 'Today’s Queue', icon: <QueueIcon />, path: '/queue' },
          { text: 'Insights', icon: <InsightsIcon />, path: '/insights' },
          ...(providersCanAddPatients
            ? [{ text: 'Add Patient', icon: <PersonAddIcon />, path: '/add-patient' }]
            : []),
        ];

  const staffMenuItems = [
    { text: 'Manage Patients', icon: <PersonAddIcon />, path: '/manage-patients' },
    { text: 'Appointment Requests', icon: <AppointmentIcon />, path: '/appointment-requests' },
  ];

  // A region admin's region can have a practice of its own (its providers, patients and
  // sessions). Those screens need a session AT that practice, so each item switches in
  // and lands on the page — setting the practice up first if it doesn't exist yet.
  // A platform administrator inside a region gets these too: the practice is beneath the
  // region, so opening it is a fresh, audited entry like any other.
  const isRegionAdmin = user?.role === 'region_admin';
  const [openingPractice, setOpeningPractice] = useState(null);
  const regionPracticeItems = [
    { text: 'Patients', icon: <PeopleIcon />, path: '/patients' },
    { text: 'Today’s Queue', icon: <QueueIcon />, path: '/queue' },
    { text: 'Providers', icon: <ProvidersIcon />, path: '/providers' },
    { text: 'Staff', icon: <StaffIcon />, path: '/staff' },
    { text: 'Manage Patients', icon: <PersonAddIcon />, path: '/manage-patients' },
  ];
  const openRegionPractice = async (item) => {
    setOpeningPractice(item.path);
    try {
      const { data: practice } = await axios.post(`${API_URL}/api/v1/hierarchy/region-practice`);
      const { data } = await axios.post(`${API_URL}/api/v1/auth/switch-portal`, {
        role: 'clinic_admin', clinic_id: practice.id,
      });
      window.location.assign(`${data.handoff_url}&next=${encodeURIComponent(item.path)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open the region practice');
      setOpeningPractice(null);
    }
  };

  // Working inside an office — or a region or organisation — opened from a level above:
  // the way back up to the level they run. PortalSwitcher can't offer it — it lists
  // memberships, and the session they are in is derived from one, not one of its own.
  const LEVEL_ROLES = ['enterprise_admin', 'org_admin', 'region_admin'];
  const mbs = Array.isArray(user?.mbs) ? user.mbs : [];
  const hereNode = String(user?.node_id || user?.org_id || '');
  const heldHere = mbs.some((m) => m.role === user?.role
    && String(m.node_id || m.organization_id || '') === hereNode);
  const openedFromAbove = (isNodeAdmin || isOrgAdmin) ? !heldHere : true;
  const backToLevels = (!isSuperAdmin && !isPlatformEntry && openedFromAbove)
    ? mbs.filter((m) => LEVEL_ROLES.includes(m.role) && (m.node_id || m.organization_id))
    : [];
  const [returning, setReturning] = useState(null);
  const backTo = async (m) => {
    const key = m.node_id || m.organization_id;
    setReturning(key);
    try {
      const { data } = await axios.post(`${API_URL}/api/v1/auth/switch-portal`,
        m.role === 'org_admin'
          ? { role: 'org_admin', organization_id: m.organization_id || m.node_id }
          : { role: m.role, node_id: m.node_id });
      window.location.assign(data.handoff_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not go back');
      setReturning(null);
    }
  };

  const adminMenuItems = [
    { text: 'Providers', icon: <ProvidersIcon />, path: '/providers' },
    { text: 'Staff', icon: <StaffIcon />, path: '/staff' },
    { text: 'Administrators', icon: <AdminRoleIcon />, path: '/administrators' },
    { text: 'Manage Patients', icon: <PersonAddIcon />, path: '/manage-patients' },
    { text: 'Appointment Requests', icon: <AppointmentIcon />, path: '/appointment-requests' },
    { text: 'Custom API', icon: <SettingsIcon />, path: '/custom-api' },
    { text: 'AI Sessions', icon: <CreditIcon />, path: '/credits' },
    // Only at the organisation's main site: opening a second location is a decision
    // for the practice the account was created from, not for a branch of it.
    ...(managesSites
      ? [{ text: 'Sites', icon: <SitesIcon />, path: '/org/sites' }]
      : []),
  ];

  const drawer = (
    <Box
      sx={{
        // The drawer paper is a flex column AND the scroll container. Two things bite:
        // a fixed height:100% is one screen tall, so a longer menu scrolls off the end
        // of the gradient; and as a flex item this box is shrunk to the paper's height
        // by the default flex-shrink:1, leaving the list to overflow past the painted
        // background. minHeight sets the floor, flexShrink:0 stops the shrink, and
        // together they make the gradient span the full menu however long it gets.
        minHeight: '100%',
        flexShrink: 0,
        background: 'linear-gradient(180deg, #042f2e 0%, #115e59 55%, #0d9488 100%)',
        borderRight: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <Toolbar sx={{ px: 2.5, pt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 28px rgba(20, 184, 166, 0.35)',
            }}
          >
            <Box component="img" src={`${process.env.PUBLIC_URL}/favicon-192.png`} alt="" sx={{ width: 32, height: 32 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"Source Serif 4", Georgia, serif',
                fontWeight: 700,
                color: '#ecfdf5',
                lineHeight: 1.15,
              }}
            >
              ScribeLive
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(236, 253, 245, 0.65)' }}>
              {isSuperAdmin ? 'Platform overview'
                : isNodeAdmin ? (user?.node_scope === 'enterprise' ? 'Enterprise' : 'Region')
                : isOrgAdmin ? 'Organisation' : 'Provider portal'}
            </Typography>
          </Box>
        </Box>
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
      <List sx={{ px: 1.5, py: 2 }}>
        {backToLevels.map((m) => {
          const key = m.node_id || m.organization_id;
          return (
            <ListItem key={`back-${key}`} disablePadding sx={{ mb: 1.5 }}>
              <ListItemButton disabled={Boolean(returning)} onClick={() => backTo(m)}
                sx={{ borderRadius: 2, color: '#ecfdf5', border: '1px solid rgba(94, 234, 212, 0.35)',
                      '&:hover': { bgcolor: 'rgba(4, 47, 46, 0.28)' } }}>
                <ListItemIcon sx={{ minWidth: 40, color: '#5eead4' }}><BackIcon /></ListItemIcon>
                <ListItemText
                  primary={returning === key ? 'Going back…' : `Back to ${m.node_name || 'your organisation'}`}
                  secondary={{ enterprise_admin: 'Enterprise', org_admin: 'Organisation' }[m.role] || 'Region'}
                  primaryTypographyProps={{ fontWeight: 700, noWrap: true }}
                  secondaryTypographyProps={{ sx: { color: 'rgba(236,253,245,0.6)', fontSize: 11 } }} />
              </ListItemButton>
            </ListItem>
          );
        })}
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                borderRadius: 2,
                color: 'rgba(236, 253, 245, 0.85)',
                '&:hover': { bgcolor: 'rgba(4, 47, 46, 0.28)' },
                // The drawer runs #042f2e -> #0d9488 top to bottom, so a selected pill
                // that LIGHTENS the background disappears against the bright end of
                // that gradient — pale fill under near-white text. Darkening instead
                // keeps the same look at the top and stays legible at the bottom,
                // however long the menu grows.
                '&.Mui-selected': {
                  bgcolor: 'rgba(4, 47, 46, 0.5)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(4, 47, 46, 0.62)' },
                  '& .MuiListItemIcon-root': { color: '#99f6e4' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: 600 }} />
            </ListItemButton>
          </ListItem>
        ))}

        {isRegionAdmin && (
          <>
            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
            <Typography variant="caption" sx={{ px: 2, color: 'rgba(236,253,245,0.55)', fontWeight: 700, letterSpacing: 1 }}>
              REGION PRACTICE
            </Typography>
            {regionPracticeItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5, mt: 1 }}>
                <ListItemButton
                  disabled={Boolean(openingPractice)}
                  onClick={() => openRegionPractice(item)}
                  sx={{
                    borderRadius: 2,
                    color: 'rgba(236, 253, 245, 0.85)',
                    '&:hover': { bgcolor: 'rgba(4, 47, 46, 0.28)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={openingPractice === item.path ? 'Opening…' : item.text}
                    primaryTypographyProps={{ fontWeight: 600 }} />
                </ListItemButton>
              </ListItem>
            ))}
          </>
        )}

        {(isAdmin || isStaff) && !isSuperAdmin && (
          <>
            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
            <Typography variant="caption" sx={{ px: 2, color: 'rgba(236,253,245,0.55)', fontWeight: 700, letterSpacing: 1 }}>
              {isAdmin ? 'CLINIC ADMIN' : 'STAFF'}
            </Typography>
            {(isAdmin ? adminMenuItems : staffMenuItems).map((item) => (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5, mt: 1 }}>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: 2,
                    color: 'rgba(236, 253, 245, 0.85)',
                    '&:hover': { bgcolor: 'rgba(4, 47, 46, 0.28)' },
                    // Darkens rather than lightens — see the note on the main nav
                    // above. This is the list that exposed it: its last entries sit
                    // over the pale bottom of the drawer gradient.
                    '&.Mui-selected': {
                      bgcolor: 'rgba(4, 47, 46, 0.5)',
                      color: '#fff',
                      borderLeft: '3px solid #5eead4',
                      pl: 1.5,
                      '&:hover': { bgcolor: 'rgba(4, 47, 46, 0.62)' },
                      '& .MuiListItemIcon-root': { color: '#5eead4' },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: 600 }} />
                </ListItemButton>
              </ListItem>
            ))}
          </>
        )}
      </List>
    </Box>
  );

  const layout = (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(4, 47, 46, 0.08)',
          color: 'text.primary',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {/* Which clinic/hospital this session is in — and, for anyone who works at
              more than one, the picker for moving between them. */}
          <SiteContext />
          {/* ml: auto keeps this in the right-hand corner even when there is nothing to
              its left — space-between alone floats a lone child to the start. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
            {/* A platform-entry session holds no other memberships, and its
                notifications would be the operator's own, not this clinic's. */}
            {!isSuperAdmin && !isPlatformEntry && <PortalSwitcher />}
            {/* Notifications are clinic-scoped. An organisation session names no
                clinic, so the bell would poll a clinic endpoint and 403 on a loop. */}
            {!isSuperAdmin && !isOrgAdmin && !isNodeAdmin && !isPlatformEntry && <NotificationBell />}
            {/* On a phone this is the first thing to go: you know who you are, and
                the avatar beside it already says so. Dropping it gives the clinic
                name — which you might genuinely need to check — room to fit. */}
            <Box sx={{ textAlign: 'right', lineHeight: 1.1, display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {user?.full_name}
              </Typography>
              {roleTags.length > 0 && (
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ mt: 0.25 }}>
                  {roleTags.map((t) => (
                    <Chip key={t.label} label={t.label} size="small"
                      color={t.active ? 'primary' : 'default'}
                      variant={t.active ? 'filled' : 'outlined'}
                      sx={{ height: 18, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }} />
                  ))}
                </Stack>
              )}
            </Box>
            <IconButton onClick={handleProfileMenuOpen} size="small">
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontWeight: 700 }}>
                {user?.full_name?.charAt(0)}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <ProfileSettingsIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* The permanent drawer is 276px wide. Holding it open from MUI's `sm`
          (600px) left barely 320px of content: the clinic name truncated, the
          user's name wrapped over three lines, cards crushed to a sliver. It now
          waits for `md` (900px); below that the same nav is one tap away on the
          hamburger, which appears at exactly the same breakpoint. */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box', width: drawerWidth, border: 0,
              // Backstop behind the gradient above. The gradient's END colour, not its
              // start: the only place paper can show is past the bottom of the content
              // (overscroll), where the drawer is at its lightest — so this blends
              // instead of banding.
              bgcolor: '#0d9488',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box', width: drawerWidth, border: 0,
              // Backstop behind the gradient above. The gradient's END colour, not its
              // start: the only place paper can show is past the bottom of the content
              // (overscroll), where the drawer is at its lightest — so this blends
              // instead of banding.
              bgcolor: '#0d9488',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // A flex item defaults to min-width:auto, meaning it refuses to shrink below
          // its own content. Any page holding something intrinsically wide — a Grid
          // container carries a negative margin, a table has a minimum — stretched this
          // box past the viewport and pushed the page sideways instead of fitting.
          // Protocols overflowed by 40px on a phone that way, cutting off the button,
          // the status chips and the right edge of every card.
          minWidth: 0,
          p: { xs: 2, sm: 3, md: 4 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          minHeight: '100vh',
          backgroundColor: '#ecfdf5',
          backgroundImage: `
            radial-gradient(circle at 20% 30%, rgba(13, 148, 136, 0.12) 0%, transparent 45%),
            radial-gradient(circle at 85% 10%, rgba(4, 47, 46, 0.06) 0%, transparent 40%),
            repeating-linear-gradient(
              -12deg,
              transparent,
              transparent 40px,
              rgba(4, 47, 46, 0.02) 40px,
              rgba(4, 47, 46, 0.02) 41px
            )
          `,
        }}
      >
        <PlatformEntryBanner />
        <Outlet />
      </Box>
    </Box>
  );

  // Same reason as the bell above: no platform, organisation, enterprise or region
  // session has a clinic for the notifications provider to poll for.
  return (isSuperAdmin || isOrgAdmin || isNodeAdmin)
    ? layout
    : <NotificationsProvider>{layout}</NotificationsProvider>;
}
